import { Box } from '@mui/material';
import { type JSX, useEffect, useState } from 'react';
import { generatePath } from 'react-router';

import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { getAllCurrentFoldersForOrganization } from 'Api/UcrmTprm/UcrmTprmApi';
import { Alert } from 'Components/Alert/Alert';
import { Button } from 'Components/Buttons/Buttons';
import { Switch } from 'Components/Buttons/Switch';
import { Checkbox } from 'Components/Checkbox/Checkbox';
import { Modal } from 'Components/Modal/Modal';
import { Text } from 'Components/Text/Text';
import { GENERIC_ERROR_MESSAGE } from 'Config/Errors';
import { ICON_CLOSE } from 'Config/Icons';
import { TPRM_FOLDER_DETAILS, UCRM_FOLDER_DETAILS } from 'Config/PathTemplates';
import { getExceptionDetailsUrl, getIssueDetailsUrl } from 'Helpers/URLBuilder/URLBuilder';
import { AssessmentSubjectActiveStateSelections, OrganizationResponseWithAssessmentSubjectsBase, SetActiveStatesRequest } from 'Models/AssessmentSubject';
import { ExceptionStatus } from 'Models/Exceptions';
import { FolderVersion } from 'Models/Folders';
import { IssueStatus } from 'Models/Issues';
import { ActiveState } from 'Models/Organization';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { getOrganizationAssessmentSubjects } from 'Models/UcrmTprm';

import styles from './SetActiveStatesModal.module.css';

export interface SetActiveStatesModalProps {
    hideModal: () => void;
    module: DueDiligenceModule;
    moduleApi: ClientTPRMApi | ClientUcrmApi;
    onActiveStatesSubmitted: () => Promise<void>;
    organization: OrganizationResponseWithAssessmentSubjectsBase;
}

/**
 * Allows the user to toggle an organization (a TPRM third party or UCRM business) and/or its assessment subjects (services or use cases) between active and inactive.
 * If the organization is toggled to inactive, allows the user to select which issues and exceptions should be closed/deleted, as well as folders with expiration dates that should be removed.
 * If an assessment subject is toggled to inactive, allows the user to select whether due dates for the assessment subject are removed (if present).
 */
export const SetActiveStatesModal = (props: SetActiveStatesModalProps): JSX.Element => {
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string>();
    const [failureMessage, setFailureMessage] = useState<string>();

    const [organizationActiveState, setOrganizationActiveState] = useState<ActiveState>(props.organization.active_state);

    const [idsOfIssuesToClose, setIdsOfIssuesToClose] = useState<Set<string>>(() => {
        const openIssues = props.organization.issues.filter((issue) => issue.status !== IssueStatus.CLOSED);
        return new Set(openIssues.map((issue) => issue.id));
    });

    const [idsOfExceptionsToClose, setIdsOfExceptionsToClose] = useState<Set<string>>(() => {
        const openExceptions = props.organization.exceptions.filter((exception) => exception.status !== ExceptionStatus.CLOSED);
        return new Set(openExceptions.map((exception) => exception.id));
    });

    const [idsOfFoldersFromWhichToRemoveExpirationDates, setIdsOfFoldersFromWhichToRemoveExpirationDates] = useState<Set<string>>();

    const [assessmentSubjectSelections, setAssessmentSubjectSelections] = useState<{ [assessmentSubjectId: string]: AssessmentSubjectActiveStateSelections }>(Object.fromEntries(getOrganizationAssessmentSubjects(props.module, props.organization).map((assessmentSubject) => [assessmentSubject.id, { active_state: assessmentSubject.active_state, remove_due_dates: true }])));

    const [folders, setFolders] = useState<FolderVersion[]>();
    const [loadingErrorOccurred, setLoadingErrorOccurred] = useState<boolean>(false);

    useEffect(() => {
        const getFolders = async () => {
            try {
                const response = await getAllCurrentFoldersForOrganization(props.moduleApi, props.organization.id);
                setFolders(response.data);
                const foldersWithExpiry = response.data.filter((f) => f.expiration_date !== undefined);
                setIdsOfFoldersFromWhichToRemoveExpirationDates(new Set(foldersWithExpiry.map((f) => f.id)));
            } catch {
                setLoadingErrorOccurred(true);
            }
        };
        getFolders();
    }, [props.moduleApi, props.organization.id]);

    const handleOrganizationToggle = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean): void => {
        setOrganizationActiveState(checked ? ActiveState.ACTIVE : ActiveState.INACTIVE);
        if (!checked) {
            setAssessmentSubjectSelections((prev) => Object.fromEntries(Object.keys(prev).map((id) => [id, { active_state: ActiveState.INACTIVE, remove_due_dates: true }])));
        }
    };

    const handleAssessmentSubjectToggle = (assessmentSubjectId: string, checked: boolean): void => {
        setAssessmentSubjectSelections((prev) => ({
            ...prev,
            [assessmentSubjectId]: checked ? { active_state: ActiveState.ACTIVE, remove_due_dates: false } : { active_state: ActiveState.INACTIVE, remove_due_dates: true },
        }));
    };

    const handleIssueCheckboxToggle = (issueId: string, checked: boolean): void => {
        setIdsOfIssuesToClose((prev) => {
            const next = new Set(prev);
            checked ? next.add(issueId) : next.delete(issueId);
            return next;
        });
    };

    const handleExceptionCheckboxToggle = (exceptionId: string, checked: boolean): void => {
        setIdsOfExceptionsToClose((prev) => {
            const next = new Set(prev);
            checked ? next.add(exceptionId) : next.delete(exceptionId);
            return next;
        });
    };

    const handleFolderCheckboxToggle = (folderId: string, checked: boolean): void => {
        setIdsOfFoldersFromWhichToRemoveExpirationDates((prev) => {
            const next = new Set(prev);
            checked ? next.add(folderId) : next.delete(folderId);
            return next;
        });
    };

    const handleAssessmentSubjectDueDatesCheckboxToggle = (assessmentSubjectId: string, checked: boolean): void => {
        setAssessmentSubjectSelections((prev) => ({
            ...prev,
            [assessmentSubjectId]: { active_state: ActiveState.INACTIVE, remove_due_dates: checked },
        }));
    };

    if (loadingErrorOccurred) {
        return (
            <Modal open>
                <Text>{GENERIC_ERROR_MESSAGE}</Text>
            </Modal>
        );
    }

    if (folders === undefined || idsOfFoldersFromWhichToRemoveExpirationDates === undefined) {
        return <Modal open isLoading closeModal={props.hideModal} header="Toggle Active/Inactive Statuses" failureMessage={failureMessage} />;
    }

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();
        setFailureMessage(undefined);
        setSuccessMessage(undefined);

        const request: SetActiveStatesRequest = {
            ids_of_exceptions_to_close: organizationActiveState === ActiveState.INACTIVE ? Array.from(idsOfExceptionsToClose) : [],
            ids_of_folders_from_which_to_remove_expiration_dates: organizationActiveState === ActiveState.INACTIVE ? Array.from(idsOfFoldersFromWhichToRemoveExpirationDates) : [],
            ids_of_issues_to_close: organizationActiveState === ActiveState.INACTIVE ? Array.from(idsOfIssuesToClose) : [],
            organization_active_state: organizationActiveState,
            assessment_subject_selections: assessmentSubjectSelections,
        };

        try {
            setIsSubmitting(true);
            await props.moduleApi.setActiveStates(request, props.organization.id);
            await props.onActiveStatesSubmitted();
            setSuccessMessage('Changes saved.');
        } catch (error) {
            setFailureMessage(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const organizationToggledFromActiveToInactive = props.organization.active_state === ActiveState.ACTIVE && organizationActiveState === ActiveState.INACTIVE;
    const openIssues = props.organization.issues.filter((issue) => issue.status !== IssueStatus.CLOSED);
    const openExceptions = props.organization.exceptions.filter((exception) => exception.status !== ExceptionStatus.CLOSED);
    const foldersWithExpirationDates = folders.filter((folder) => folder.expiration_date !== undefined);
    const outstandingOrganizationItemsExist = organizationToggledFromActiveToInactive && (openIssues.length > 0 || openExceptions.length > 0 || foldersWithExpirationDates.length > 0);

    const assessmentSubjectsToggledFromActiveToInactive = getOrganizationAssessmentSubjects(props.module, props.organization).filter((assessmentSubject) => assessmentSubject.active_state === ActiveState.ACTIVE && assessmentSubjectSelections[assessmentSubject.id].active_state === ActiveState.INACTIVE);

    const terms = props.module === ProductModule.UCRM ? { organization: 'business', organizationTitle: 'Business', subject: 'use case', subjectsTitle: 'Use Cases' } : { organization: 'third party', organizationTitle: 'Third Party', subject: 'third-party service', subjectsTitle: 'Third-Party Services' };

    return (
        <Modal open header="Toggle Active/Inactive Statuses">
            {successMessage && <Alert type="success">{successMessage}</Alert>}
            {failureMessage && <Alert type="error">{failureMessage}</Alert>}

            <Box component="form" noValidate onSubmit={handleSubmit}>
                <div className={styles.section}>
                    <Text variant="Header3">{terms.organizationTitle}</Text>
                    <Text>{`Setting the ${terms.organization} as inactive will cause it to be hidden by default on this page. If the ${terms.organization} is inactive, all of its ${terms.subject}s must also be inactive.`}</Text>
                    <div className={styles.switchRow}>
                        <Switch checked={organizationActiveState === ActiveState.ACTIVE} onChange={handleOrganizationToggle} />
                        <Text noStyles>{props.organization.name}</Text>
                    </div>
                </div>

                <div className={styles.section}>
                    <Text variant="Header3">{terms.subjectsTitle}</Text>
                    <Text>{`Setting a ${terms.subject} as inactive will cause it to be hidden by default on this page.`}</Text>
                    {[...getOrganizationAssessmentSubjects(props.module, props.organization)]
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((assessmentSubject) => (
                            <div className={styles.switchRow} key={assessmentSubject.id}>
                                <Switch checked={assessmentSubjectSelections[assessmentSubject.id].active_state === ActiveState.ACTIVE} onChange={(_event, checked) => handleAssessmentSubjectToggle(assessmentSubject.id, checked)} disabled={organizationActiveState === ActiveState.INACTIVE} />
                                <Text noStyles>{assessmentSubject.name}</Text>
                            </div>
                        ))}
                </div>

                {outstandingOrganizationItemsExist && (
                    <div className={styles.section}>
                        <Text variant="Header3">{`Outstanding Items for ${terms.organizationTitle}`}</Text>

                        {openIssues.length > 0 && (
                            <div className={styles.section}>
                                <Text variant="Header4">Open Issues</Text>
                                <Text noStyles>Each selected issue will be deleted if it is a draft, or closed if it is not a draft.</Text>
                                {openIssues
                                    .sort((a, b) => a.title.localeCompare(b.title))
                                    .map((issue) => (
                                        <Checkbox key={issue.id} checked={idsOfIssuesToClose.has(issue.id)} onToggled={(checked) => handleIssueCheckboxToggle(issue.id, checked)} label={issue.title} labelLinkDestination={getIssueDetailsUrl(issue.id, props.module)} />
                                    ))}
                            </div>
                        )}

                        {openExceptions.length > 0 && (
                            <div className={styles.section}>
                                <Text variant="Header4">Open Exceptions</Text>
                                <Text noStyles>Each selected exception will be deleted if it is a draft, or closed if it is not a draft.</Text>
                                {openExceptions
                                    .sort((a, b) => a.title.localeCompare(b.title))
                                    .map((exception) => (
                                        <Checkbox key={exception.id} checked={idsOfExceptionsToClose.has(exception.id)} onToggled={(checked) => handleExceptionCheckboxToggle(exception.id, checked)} label={exception.title} labelLinkDestination={getExceptionDetailsUrl(exception.id, props.module)} />
                                    ))}
                            </div>
                        )}

                        {foldersWithExpirationDates.length > 0 && (
                            <div className={styles.section}>
                                <Text variant="Header4">Folders with Expiration Dates</Text>
                                <Text noStyles>Each selected folder will have its expiration date removed.</Text>
                                {foldersWithExpirationDates
                                    .sort((a, b) => a.name.localeCompare(b.name))
                                    .map((folder) => (
                                        <Checkbox key={folder.id} checked={idsOfFoldersFromWhichToRemoveExpirationDates.has(folder.id)} onToggled={(checked) => handleFolderCheckboxToggle(folder.id, checked)} label={folder.name} labelLinkDestination={generatePath(props.module === ProductModule.UCRM ? UCRM_FOLDER_DETAILS : TPRM_FOLDER_DETAILS, { third_party_id: props.organization.id, folder_id: folder.id })} />
                                    ))}
                            </div>
                        )}
                    </div>
                )}

                {assessmentSubjectsToggledFromActiveToInactive.length > 0 && (
                    <div className={styles.section}>
                        <Text variant="Header3">{`Outstanding Items for ${terms.subjectsTitle}`}</Text>
                        <Text noStyles>{`Each selected ${props.module === ProductModule.UCRM ? 'use case' : 'service'} will have all associated due dates removed.`}</Text>
                        {assessmentSubjectsToggledFromActiveToInactive.map((assessmentSubject) => (
                            <Checkbox key={assessmentSubject.id} checked={assessmentSubjectSelections[assessmentSubject.id].remove_due_dates} onToggled={(checked) => handleAssessmentSubjectDueDatesCheckboxToggle(assessmentSubject.id, checked)} label={assessmentSubject.name} />
                        ))}
                    </div>
                )}

                <div className={'modalFormButtonContainer'}>
                    <Button variant="secondary" onClick={props.hideModal} disabled={isSubmitting} fontAwesomeImage={ICON_CLOSE}>
                        Close
                    </Button>
                    <Button variant="submit" isLoading={isSubmitting} loadingText="Saving...">
                        Save
                    </Button>
                </div>
            </Box>
        </Modal>
    );
};
